"use client"

import { useEffect } from "react"
import { usePathname, useRouter } from "next/navigation"
import { AuthProvider, useAuth } from "@/lib/auth-context"
import { Sidebar } from "@/components/dashboard/sidebar"

const PUBLIC_ROUTES = ["/login", "/mfa", "/forgot-password", "/reset-password", "/auth/callback"]
const NO_LAYOUT_ROUTES = ["/voucher-print"]

function isPublicRoute(pathname: string) {
  return PUBLIC_ROUTES.some((route) => pathname === route || pathname.startsWith(route + "/"))
}

function isNoLayoutRoute(pathname: string) {
  return NO_LAYOUT_ROUTES.some((route) => pathname.startsWith(route))
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isLoading, isLoggedIn } = useAuth()
  const pathname = usePathname()
  const router = useRouter()

  useEffect(() => {
    if (!isLoading && !isLoggedIn && !isPublicRoute(pathname)) {
      router.replace("/login")
    }
  }, [isLoading, isLoggedIn, pathname, router])

  const isPublic = isPublicRoute(pathname)
  const isNoLayout = isNoLayoutRoute(pathname)
  const showDashboardLayout = isLoggedIn && !isPublic && !isNoLayout
  const showLoading = isLoading || (!isLoggedIn && !isPublic)

  // Always return the same root element type to prevent "removeChild" errors.
  // Switching between null / Fragment / div causes React DOM reconciliation to
  // conflict with portal-based Radix UI components (Dialog, Select, Sheet, etc.).
  return (
    <div className={showDashboardLayout ? "flex min-h-screen bg-background" : undefined}>
      {showDashboardLayout && <Sidebar />}
      <div className={showDashboardLayout ? "ml-64 flex flex-1 flex-col" : undefined}>
        {showLoading ? (
          <div className="flex min-h-screen items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  )
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <AuthProvider>
      <AuthGuard>{children}</AuthGuard>
    </AuthProvider>
  )
}
